import React from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@headlessui/react';
import {
    SparklesIcon, ClipboardCheckIcon, UserAddIcon, FilterIcon, MapIcon,
    UserGroupIcon, KeyIcon, XIcon,
} from '@heroicons/react/outline';
import { hasRole, ROLES } from '../../shared/Utils';

// "What's new" walkthrough. Opens once per user per release, and can be
// reopened from the header. To announce a new release, bump WHATS_NEW_RELEASE
// and edit ALL_STEPS.
export const WHATS_NEW_RELEASE = '2026-11';

const storageKey = (userId) => `whatsNewSeen:${WHATS_NEW_RELEASE}:${userId}`;

export function hasSeenWhatsNew(userId) {
    try {
        return localStorage.getItem(storageKey(userId)) === '1';
    } catch (e) {
        return true; // storage blocked: don't keep popping up
    }
}

export function markWhatsNewSeen(userId) {
    try {
        localStorage.setItem(storageKey(userId), '1');
    } catch (e) {
        // ignore
    }
}

const LEADER = [ROLES.CELL_LEADER];
const ADMIN = [ROLES.ADMIN, ROLES.SUPER_ADMIN];
const EVERYONE = [ROLES.CELL_LEADER, ROLES.ADMIN, ROLES.SUPER_ADMIN];

// Small illustrations, built from the same styles as the real screens.
const AttendancePreview = () => (
    <div className="grid grid-cols-3 gap-2" aria-hidden="true">
        {[['GO', 'Grace', true], ['AB', 'Ama', true], ['CE', 'Chidi', false]].map(([initials, name, present]) => (
            <div key={name} className={`relative flex flex-col items-center gap-1 rounded-lg border-2 px-1 py-2 ${present ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white'}`}>
                <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${present ? 'bg-emerald-500 text-white' : 'bg-sky-100 text-sky-800'}`}>{initials}</span>
                <span className="text-xs text-gray-700">{name}</span>
            </div>
        ))}
    </div>
);

const StatCardsPreview = () => (
    <div className="grid grid-cols-3 gap-2" aria-hidden="true">
        {[['Home', 12, true], ['Campus', 6, false], ['Family', 9, false]].map(([label, n, active]) => (
            <div key={label} className={`rounded-lg bg-white p-2 shadow ${active ? 'ring-2 ring-blue-500' : ''}`}>
                <p className="text-[11px] text-gray-600">{label} Cells</p>
                <p className="text-lg font-bold text-gray-900">{n}</p>
            </div>
        ))}
    </div>
);

export const ALL_STEPS = [
    {
        id: 'intro',
        roles: EVERYONE,
        icon: SparklesIcon,
        title: "What's new in Cell Report",
        body: 'A quick tour of the latest changes. It takes about a minute, and you can reopen it any time from the "What\'s new" button at the top of the page.',
    },
    {
        id: 'attendance',
        roles: LEADER,
        icon: ClipboardCheckIcon,
        title: 'Faster attendance',
        body: 'When you create or edit a report, everyone in your cell now appears as a tile on one screen.',
        points: [
            'Tap a person to mark them present. Tap again to undo.',
            'Use "Mark all present", then tap the few who were absent.',
            'The bar at the bottom shows your totals and the Save button.',
        ],
        preview: AttendancePreview,
        action: { label: 'Go to Reports', to: '/dashboard/reports' },
    },
    {
        id: 'guests',
        roles: LEADER,
        icon: UserAddIcon,
        title: 'Add first-time guests while you record',
        body: 'No need to visit the Guests page first.',
        points: [
            'In the Guests section of a report, tap "Add guest".',
            'Enter their name (phone is optional) and tap "Add as present".',
            'They are saved to your Guests list for next time.',
            'Guests you delete no longer show up on new reports.',
        ],
    },
    {
        id: 'cell-filters',
        roles: ADMIN,
        icon: FilterIcon,
        title: 'Click a summary card to filter cells',
        body: 'On the Cells page, the Home, Campus and Family cards now filter the table, the map and the Excel export.',
        points: [
            'Pick several cards to combine them, for example Home and Family cells.',
            'Click a card again to remove it, or "Total Cells" to show everything.',
            'Filters stay when you refresh, and you can share the page link with them.',
        ],
        preview: StatCardsPreview,
        action: { label: 'Go to Cells', to: '/dashboard/cells' },
    },
    {
        id: 'map',
        roles: ADMIN,
        icon: MapIcon,
        title: 'A better cell map',
        body: 'Switch the Cells page to Map to see where every cell meets.',
        points: [
            'Scroll over the map to zoom in and out.',
            'Cells are placed from their address. If the address can\'t be found, the cell is placed at the centre of its city until you update it. Notes like "/Zoom" are ignored.',
            'Cells that still can\'t be placed are listed above the map. Click a name to fix its address or city.',
        ],
        action: { label: 'Open the map', to: '/dashboard/cells?view=map' },
    },
    {
        id: 'admin-leaders',
        roles: ADMIN,
        icon: UserGroupIcon,
        title: 'Admins can lead cells',
        body: 'The "Cell Leader" list on the cell form now includes admins and super admins.',
        points: [
            'Picking an admin gives them the leader screens for that cell.',
            'If the cell later moves to someone else, they stop being a leader automatically, unless they were made a leader on purpose.',
        ],
    },
    {
        id: 'sso',
        roles: EVERYONE,
        requiresSso: true,
        icon: KeyIcon,
        title: 'Sign in with Google or Apple',
        body: 'Link your Google or Apple account so you can sign in without a password. Password sign-in will be retired soon.',
        points: [
            'Go to Settings → Sign-in Methods and choose Link.',
            'Use the account with the same email you use for Cell Report.',
        ],
        action: { label: 'Link my account', to: '/dashboard/settings?tab=signin' },
    },
];

export function stepsFor(user, { ssoEnabled = false } = {}) {
    return ALL_STEPS.filter((step) => hasRole(user, ...step.roles) && (!step.requiresSso || ssoEnabled));
}

const WhatsNewDialog = ({ open, onClose, steps, onNavigate }) => {
    const [index, setIndex] = React.useState(0);

    React.useEffect(() => {
        if (open) setIndex(0);
    }, [open]);

    if (steps.length === 0) return null;
    const step = steps[Math.min(index, steps.length - 1)];
    const isFirst = index === 0;
    const isLast = index >= steps.length - 1;
    const Icon = step.icon;
    const Preview = step.preview;

    return (
        <Dialog open={open} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-gray-900/40" aria-hidden="true" />
            <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
                <Dialog.Panel className="w-full max-w-lg rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
                    <div className="flex items-start justify-between gap-3 px-6 pt-6">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                            <Icon className="h-6 w-6" aria-hidden="true" />
                        </span>
                        <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                            <XIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>

                    <div className="px-6 pb-2 pt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                            {isFirst ? "What's new" : `Step ${index} of ${steps.length - 1}`}
                        </p>
                        <Dialog.Title className="mt-1 text-xl font-semibold text-gray-900">{step.title}</Dialog.Title>
                        <Dialog.Description className="mt-2 text-sm text-gray-600">{step.body}</Dialog.Description>

                        {isFirst && steps.length > 1 && (
                            <ul className="mt-4 space-y-1.5 text-sm text-gray-800">
                                {steps.slice(1).map((s) => (
                                    <li key={s.id} className="flex items-center gap-2">
                                        <s.icon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                                        {s.title}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {step.points && (
                            <ul className="mt-4 list-disc space-y-1.5 pl-5 text-sm text-gray-800">
                                {step.points.map((point) => <li key={point}>{point}</li>)}
                            </ul>
                        )}

                        {Preview && (
                            <div className="mt-4 rounded-xl bg-gray-50 p-3">
                                <Preview />
                            </div>
                        )}

                        {step.action && (
                            <button
                                type="button"
                                onClick={() => onNavigate(step.action.to)}
                                className="mt-4 text-sm font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
                            >
                                {step.action.label} →
                            </button>
                        )}
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
                        <div className="flex gap-1.5" aria-hidden="true">
                            {steps.map((s, i) => (
                                <span key={s.id} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-5 bg-emerald-600' : 'w-1.5 bg-gray-300'}`} />
                            ))}
                        </div>
                        <div className="flex gap-2">
                            {isFirst ? (
                                <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
                                    Skip
                                </button>
                            ) : (
                                <button type="button" onClick={() => setIndex(index - 1)} className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
                                    Back
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => (isLast ? onClose() : setIndex(index + 1))}
                                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                            >
                                {isLast ? 'Done' : isFirst ? 'Show me' : 'Next'}
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

WhatsNewDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    steps: PropTypes.array.isRequired,
    onNavigate: PropTypes.func.isRequired,
};

export default WhatsNewDialog;
